import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, Search, User, LogOut, Settings, Bell, Users, Home, Heart, X, 
  Download, Tv, Film, Star, Clock, TrendingUp 
} from 'lucide-react';

const Header = ({ 
  user, 
  profiles, 
  currentProfileId, 
  onProfileChange, 
  onGoToMyList, 
  onGoToHome, 
  isMyListView = false,
  allMovies = [],
  onPlayVideo,
  // Nuevas props para funcionalidades
  onSearchOpen,
  onAuthOpen,
  onNotificationsOpen,
  onProfileManagerOpen,
  onDownloadsOpen,
  onSettingsOpen,
  unreadNotificationsCount = 0
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfilesOpen, setIsProfilesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Refs para detectar clicks fuera de los modals
  const profileModalRef = useRef(null);
  const profilesModalRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const handleClickOutside = (event) => {
      if (profileModalRef.current && !profileModalRef.current.contains(event.target)) {
        const profileButton = event.target.closest('[data-profile-button]');
        if (!profileButton) {
          setIsProfileOpen(false);
        }
      }
      
      if (profilesModalRef.current && !profilesModalRef.current.contains(event.target)) {
        setIsProfilesOpen(false);
      }
      
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const mobileMenuButton = event.target.closest('[data-mobile-menu-button]');
        if (!mobileMenuButton) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleProfile = () => {
    setIsProfileOpen(prev => !prev);
    setIsProfilesOpen(false);
  };

  const toggleProfiles = () => {
    setIsProfilesOpen(prev => !prev);
    setIsProfileOpen(false);
  };

  const handleProfileChange = (profileId) => {
    onProfileChange(profileId);
    setIsProfilesOpen(false);
    setIsProfileOpen(false);
  };

  const closeAllModals = () => {
    setIsProfileOpen(false);
    setIsProfilesOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    onGoToHome();
    closeAllModals();
  };

  const headerClass = `fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
    isScrolled 
      ? 'bg-black/95 backdrop-blur-xl border-b border-gray-800/50 shadow-2xl' 
      : 'bg-gradient-to-b from-black/80 to-transparent backdrop-blur-sm'
  }`;

  return (
    <motion.header
      className={headerClass}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-6">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <motion.button
                onClick={handleHomeClick}
                className="p-2 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Home className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={handleHomeClick}
                className="text-2xl font-black bg-gradient-to-r from-red-400 via-pink-400 to-purple-400 bg-clip-text text-transparent cursor-pointer"
                whileHover={{ scale: 1.02 }}
              >
                StreamFusion
              </motion.button>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <motion.button 
                className="text-gray-300 hover:text-white transition-colors font-semibold text-sm uppercase tracking-wide"
                whileHover={{ y: -1 }}
                onClick={handleHomeClick}
              >
                Inicio
              </motion.button>
              <motion.button 
                className="text-gray-300 hover:text-white transition-colors font-semibold text-sm uppercase tracking-wide flex items-center gap-1"
                whileHover={{ y: -1 }}
                onClick={closeAllModals}
              >
                <Tv className="w-4 h-4" />
                Series
              </motion.button>
              <motion.button 
                className="text-gray-300 hover:text-white transition-colors font-semibold text-sm uppercase tracking-wide flex items-center gap-1"
                whileHover={{ y: -1 }}
                onClick={closeAllModals}
              >
                <Film className="w-4 h-4" />
                Películas
              </motion.button>
              <motion.button 
                className="text-gray-300 hover:text-white transition-colors font-semibold text-sm uppercase tracking-wide flex items-center gap-1"
                whileHover={{ y: -1 }}
                onClick={closeAllModals}
              >
                <TrendingUp className="w-4 h-4" />
                Trending
              </motion.button>
            </nav>
          </div>

          {/* Desktop Right Section */}
          <div className="hidden lg:flex items-center gap-4">
            {/* My List */}
            <motion.button
              onClick={() => {
                onGoToMyList();
                closeAllModals();
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isMyListView 
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              whileHover={{ scale: 1.05 }}
            >
              <Heart className={`w-4 h-4 ${isMyListView ? 'fill-red-400' : ''}`} />
              <span className="font-semibold text-sm">Mi Lista</span>
            </motion.button>

            {/* Notifications */}
            <motion.button 
              className="p-2 text-gray-300 hover:text-white transition-colors relative"
              whileHover={{ scale: 1.1 }}
              onClick={onNotificationsOpen}
            >
              <Bell className="w-5 h-5" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                  {unreadNotificationsCount}
                </span>
              )}
            </motion.button>

            {/* Auth or Profile */}
            {user ? (
              <div className="relative" ref={profileModalRef}>
                <motion.div
                  data-profile-button="true"
                  className="flex items-center gap-3 cursor-pointer p-2 rounded-xl hover:bg-white/10 transition-colors"
                  onClick={toggleProfile}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className={`w-8 h-8 ${user.color} rounded-full flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold text-xs">{user.name.charAt(0)}</span>
                  </div>
                </motion.div>

                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      className="absolute top-full right-0 mt-3 w-72 bg-gray-900/95 backdrop-blur-xl border border-gray-700 rounded-xl shadow-2xl p-4 z-[1100]"
                    >
                      {/* Header del modal */}
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-white text-lg">Tu Cuenta</h4>
                        <motion.button
                          onClick={() => setIsProfileOpen(false)}
                          className="p-1 text-gray-400 hover:text-white transition-colors"
                          whileHover={{ scale: 1.1 }}
                        >
                          <X className="w-4 h-4" />
                        </motion.button>
                      </div>

                      {/* Información del usuario */}
                      <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white/5">
                        <div className={`w-12 h-12 ${user.color} rounded-full flex items-center justify-center`}>
                          <span className="text-white font-bold text-base">{user.name.charAt(0)}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-white text-sm">{user.name}</h4>
                          <p className="text-gray-400 text-xs">{user.email}</p>
                          <p className="text-green-400 text-xs font-semibold mt-1">Premium</p>
                        </div>
                      </div>

                      {/* Opciones del menú */}
                      <div className="space-y-1">
                        <button 
                          onClick={toggleProfiles}
                          className="w-full flex items-center gap-3 text-gray-300 hover:text-white p-3 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium text-left"
                        >
                          <Users className="w-4 h-4" />
                          <span>Cambiar Perfil</span>
                        </button>

                        <button 
                          onClick={onProfileManagerOpen}
                          className="w-full flex items-center gap-3 text-gray-300 hover:text-white p-3 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium text-left"
                        >
                          <User className="w-4 h-4" />
                          <span>Gestionar Perfiles</span>
                        </button>

                        <button 
                          onClick={onSettingsOpen}
                          className="w-full flex items-center gap-3 text-gray-300 hover:text-white p-3 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium text-left"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Configuración</span>
                        </button>

                        <div className="border-t border-gray-700/50 my-2"></div>

                        <button className="w-full flex items-center gap-3 text-red-400 hover:text-red-300 p-3 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium text-left">
                          <LogOut className="w-4 h-4" />
                          <span>Cerrar Sesión</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <motion.button
                onClick={onAuthOpen}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <User className="w-4 h-4" />
                Iniciar Sesión
              </motion.button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-3">
            <motion.button
              onClick={onGoToMyList}
              className="p-2 text-gray-300 hover:text-white transition-colors"
              whileTap={{ scale: 0.9 }}
            >
              <Heart className="w-5 h-5" />
            </motion.button>

            <motion.button
              data-mobile-menu-button="true"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-300 hover:text-white transition-colors"
              whileTap={{ scale: 0.9 }}
            >
              <Menu className="w-6 h-6" />
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 pb-4 border-t border-gray-700/50 pt-4 z-[1100]"
            >
              <nav className="flex flex-col gap-2">
                <button 
                  className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                  onClick={handleHomeClick}
                >
                  <Home className="w-4 h-4" />
                  Inicio
                </button>
                <button 
                  className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                  onClick={closeAllModals}
                >
                  <Tv className="w-4 h-4" />
                  Series
                </button>
                <button 
                  className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                  onClick={closeAllModals}
                >
                  <Film className="w-4 h-4" />
                  Películas
                </button>
                <button 
                  className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                  onClick={closeAllModals}
                >
                  <TrendingUp className="w-4 h-4" />
                  Trending
                </button>
                
                <div className="border-t border-gray-700/50 my-2"></div>

                {user ? (
                  <>
                    <button 
                      className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                      onClick={onProfileManagerOpen}
                    >
                      <User className="w-4 h-4" />
                      Gestionar Perfiles
                    </button>
                    <button 
                      className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                      onClick={onSettingsOpen}
                    >
                      <Settings className="w-4 h-4" />
                      Configuración
                    </button>
                  </>
                ) : (
                  <button 
                    className="text-gray-300 hover:text-white py-3 text-left font-medium px-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3"
                    onClick={onAuthOpen}
                  >
                    <User className="w-4 h-4" />
                    Iniciar Sesión
                  </button>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Profiles Modal */}
      <AnimatePresence>
        {isProfilesOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1200] flex items-center justify-center p-4"
            onClick={() => setIsProfilesOpen(false)}
          >
            <motion.div
              ref={profilesModalRef}
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-700 shadow-2xl z-[1201]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del modal de perfiles */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Seleccionar Perfil</h3>
                <motion.button
                  onClick={() => setIsProfilesOpen(false)}
                  className="p-1 text-gray-400 hover:text-white transition-colors"
                  whileHover={{ scale: 1.1 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
              
              <div className="space-y-3">
                {profiles.map(profile => (
                  <motion.button
                    key={profile.id}
                    onClick={() => handleProfileChange(profile.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                      currentProfileId === profile.id 
                        ? 'bg-gradient-to-r from-gray-700 to-gray-600 shadow-inner' 
                        : 'hover:bg-gray-800 hover:shadow-lg'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={`w-14 h-14 ${profile.color} rounded-full flex items-center justify-center shadow-lg`}>
                      <span className="text-white font-bold text-lg">{profile.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-white text-base">{profile.name}</h4>
                      <p className="text-gray-400 text-sm">{profile.email}</p>
                    </div>
                    {currentProfileId === profile.id && (
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    )}
                  </motion.button>
                ))}
              </div>
              
              {/* Footer del modal */}
              <div className="mt-6 pt-4 border-t border-gray-700/50">
                <button 
                  onClick={onProfileManagerOpen}
                  className="w-full flex items-center gap-3 text-gray-300 hover:text-white p-3 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium"
                >
                  <Users className="w-4 h-4" />
                  <span>Administrar Perfiles</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;